import type { TFunction } from "i18next";
import type { FieldError } from "react-hook-form";

/**
 * Encode an i18n key (and optional interpolation params) into a zod message.
 * Pass the result as the `message` for any zod refinement, e.g.
 *   z.string().min(3, tKey("VALIDATION_MIN_LENGTH", { count: 3 }))
 */
export const tKey = (
  key: string,
  params?: Record<string, unknown>,
): string => {
  return params ? JSON.stringify({ key, params }) : key;
};

/**
 * Translate a react-hook-form FieldError back into a localized string,
 * resolving keys (and params) produced by `tKey`. Call at render time
 * so locale switches re-render.
 */
export const resolveError = (
  error: FieldError | undefined,
  t: TFunction,
): string | undefined => {
  if (!error) return undefined;
  const raw = error.message ?? "";
  if (!raw) return undefined;
  if (raw.startsWith("{")) {
    try {
      const parsed = JSON.parse(raw) as {
        key: string;
        params?: Record<string, unknown>;
      };
      return t(parsed.key, parsed.params);
    } catch {
      return raw;
    }
  }
  return t(raw);
};
